import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getEnvVar } from '../../../environments/env-loader';

export interface EmergencyContact {
  id: number;
  contactName: string;
  phoneNumber: string;
  relationship?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmergencyContactRequest {
  contactName: string;
  phoneNumber: string;
  relationship?: string;
}

export interface UpdateEmergencyContactRequest {
  contactName?: string;
  phoneNumber?: string;
  relationship?: string;
}

export interface EmergencyContactListResponse {
  success: boolean;
  message: string;
  data: EmergencyContact[];
}

export interface EmergencyContactResponse {
  success: boolean;
  message: string;
  data: EmergencyContact;
}

@Injectable({
  providedIn: 'root'
})
export class EmergencyContactService {
  private readonly API_URL = getEnvVar('REACT_APP_API_URL', environment.apiUrl);

  constructor(private http: HttpClient) {}

  /**
   * Get all emergency contacts for the current user
   */
  getEmergencyContacts(): Observable<EmergencyContactListResponse> {
    return this.http.get<EmergencyContactListResponse>(`${this.API_URL}/emergency-contacts`);
  }

  /**
   * Get a specific emergency contact by ID
   */
  getEmergencyContact(id: number): Observable<EmergencyContactResponse> {
    return this.http.get<EmergencyContactResponse>(`${this.API_URL}/emergency-contacts/${id}`);
  }

  /**
   * Create a new emergency contact
   */
  createEmergencyContact(contactData: CreateEmergencyContactRequest): Observable<EmergencyContactResponse> {
    return this.http.post<EmergencyContactResponse>(`${this.API_URL}/emergency-contacts`, contactData);
  }

  /**
   * Update an existing emergency contact
   */
  updateEmergencyContact(id: number, contactData: UpdateEmergencyContactRequest): Observable<EmergencyContactResponse> {
    return this.http.put<EmergencyContactResponse>(`${this.API_URL}/emergency-contacts/${id}`, contactData);
  }

  /**
   * Delete an emergency contact
   */
  deleteEmergencyContact(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.API_URL}/emergency-contacts/${id}`);
  }

  /**
   * Validate phone number format
   */
  validatePhoneNumber(phone: string): boolean {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  }
}

